import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, Sparkles, X, Film, Volume2, VolumeX, ExternalLink, Zap, Video, CheckCircle2 } from 'lucide-react';
import { AI_VIDEOS } from '../constants';
import { AiVideo } from '../types';
import { useLanguage } from '../context/LanguageContext';

const AiVideoShowcase: React.FC = () => {
  const [videoList] = useState<AiVideo[]>(AI_VIDEOS);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [activeVideo, setActiveVideo] = useState<AiVideo | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const { t, isUrdu } = useLanguage();

  const categories = [
    { key: 'All', label: t.aiVideos.filterAll },
    { key: 'AI Commercials', label: t.aiVideos.filterCommercials },
    { key: 'AI Reels & Shorts', label: t.aiVideos.filterReels },
    { key: 'Product Showcases', label: t.aiVideos.filterProducts },
  ];

  // Handle ESC key & scroll locking when modal is open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveVideo(null);
      }
    };

    if (activeVideo) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeVideo]);

  const filteredVideos = selectedCategory === 'All'
    ? videoList
    : videoList.filter(v => v.category === selectedCategory);

  return (
    <section 
      id="ai-videos" 
      aria-label="AI Video Production & Viral Reels Showcase Gallery"
      className="py-16 sm:py-24 bg-slate-50/60 text-slate-900 relative border-b border-slate-200/80 w-full scroll-mt-24 overflow-hidden"
    >
      {/* Background Decorative Ambient Elements */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-indigo-100/40 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[500px] h-[500px] bg-blue-100/30 blur-[130px] rounded-full pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:32px_32px] opacity-30 pointer-events-none" />

      {/* Full Width Container */}
      <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-20 relative z-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-10 sm:mb-12 pb-6 border-b border-slate-200/80">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-indigo-200/80 rounded-full mb-3.5 shadow-xs">
              <Sparkles size={14} className="text-indigo-600 animate-spin-slow" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-700">
                {t.aiVideos.badge}
              </span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-900 leading-tight">
              {t.aiVideos.title}
            </h2>
            <p className="text-slate-600 text-xs sm:text-sm mt-3 leading-relaxed max-w-2xl font-normal">
              {t.aiVideos.subtitle}
            </p>
          </div>

          {/* Stats / Highlight Badge */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-3 px-4 py-2.5 bg-white border border-slate-200/90 rounded-2xl shadow-xs">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100">
                <Video size={18} />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  <span>{videoList.length}+ {isUrdu ? 'پروڈکشن ویڈیوز' : 'Production Videos'}</span>
                  <CheckCircle2 size={13} className="text-emerald-600" />
                </div>
                <div className="text-[10px] font-mono text-slate-500">{isUrdu ? '4K کوالٹی • 9:16 عمودی' : '4K Ultra Quality • 9:16 Vertical'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => {
              const count = cat.key === 'All' ? videoList.length : videoList.filter(v => v.category === cat.key).length;
              const isSelected = selectedCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 scale-[1.02]'
                      : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200 shadow-2xs'
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-2 text-slate-500 text-xs font-medium">
            <Zap size={14} className="text-amber-500" />
            <span>{isUrdu ? 'مکمل ویڈیو دیکھنے کے لیے کلک کریں' : 'Click any video to play full preview'}</span>
          </div>
        </div>

        {/* Video Grid - Enhanced 9:16 Portrait Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              onClick={() => setActiveVideo(video)}
              className="group relative bg-white border border-slate-200/90 hover:border-indigo-400 rounded-2xl overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between hover:-translate-y-1"
            >
              {/* 9:16 Aspect Thumbnail Container */}
              <div className="relative aspect-[9/16] overflow-hidden bg-slate-950">
                <img
                  src={video.poster}
                  alt={video.title}
                  className="w-full h-full object-cover object-center group-hover:scale-108 transition-transform duration-700 opacity-90 group-hover:opacity-100"
                  loading="lazy"
                />
                
                {/* Subtle Cinematic Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/25 to-slate-950/40 opacity-80 group-hover:opacity-90 transition-opacity" />
                
                {/* Top Badges */}
                <div className="absolute top-2.5 left-2.5 bg-slate-950/80 backdrop-blur-md border border-white/10 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider text-slate-200 shadow-md">
                  9:16 Reel
                </div>

                <div className="absolute top-2.5 right-2.5 bg-slate-950/80 backdrop-blur-md px-2 py-0.5 rounded-md text-[10px] font-mono font-bold text-slate-200 border border-white/10 shadow-md">
                  {video.duration}
                </div>

                {/* Center Glowing Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute -inset-2 bg-indigo-500/30 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative w-12 h-12 sm:w-13 sm:h-13 rounded-full bg-indigo-600/90 group-hover:bg-indigo-600 text-white flex items-center justify-center shadow-xl group-hover:scale-115 transition-all duration-300 border-2 border-white/40">
                      <Play size={20} className="fill-white ml-0.5" />
                    </div>
                  </div>
                </div>

                {/* Overlay Text Details on Thumbnail */}
                <div className="absolute bottom-0 left-0 right-0 p-3 space-y-1">
                  <span className="text-[8px] font-black uppercase tracking-wider text-indigo-200 bg-indigo-950/80 backdrop-blur-sm px-2 py-0.5 rounded border border-indigo-500/30 inline-block shadow-xs">
                    {video.category}
                  </span>
                  <h3 className="text-xs sm:text-sm font-bold text-white group-hover:text-indigo-200 transition-colors line-clamp-2 leading-snug drop-shadow-md">
                    {video.title}
                  </h3>
                </div>
              </div>

              {/* Bottom Card Footer */}
              <div className="p-3 bg-white flex flex-col justify-between gap-2 border-t border-slate-100">
                <p className="text-slate-600 text-[11px] leading-snug line-clamp-2 font-normal">
                  {video.description}
                </p>

                <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
                  <div className="flex flex-wrap gap-1">
                    {video.tags.slice(0, 2).map((t, idx) => (
                      <span key={idx} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                        #{t}
                      </span>
                    ))}
                  </div>
                  <span className="text-[10px] font-bold text-indigo-600 group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                    {t.aiVideos.playVideo} <Play size={8} className="fill-indigo-600" />
                  </span>
                </div>
              </div>

            </div>
          ))}
        </div>

      </div>

      {/* Video Player Modal */}
      {/* Video Modal Player via Portal */}
      {activeVideo && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 z-[99999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-fade-in overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Video Player"
          onClick={() => setActiveVideo(null)}
        >
          {/* Fixed Floating Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveVideo(null);
            }}
            aria-label="Close Video Player"
            className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[120] p-3 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white shadow-2xl border-2 border-white/30 transition-all cursor-pointer hover:scale-110 active:scale-95 flex items-center gap-1.5 font-bold text-xs"
          >
            <X size={18} />
            <span className="hidden sm:inline pr-1">{t.aiVideos.closeModal} (ESC)</span>
          </button>

          {/* Modal Container */}
          <div 
            className="relative w-full max-w-xs sm:max-w-md bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[85vh] sm:max-h-[90vh] my-auto text-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3.5 sm:p-4 border-b border-slate-200 bg-slate-50 shrink-0">
              <div className="flex items-center gap-2.5 overflow-hidden">
                <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                  <Film size={16} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight truncate">
                    {activeVideo.title}
                  </h3>
                  <p className="text-[10px] text-indigo-600 font-bold">{activeVideo.category}</p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-2 rounded-xl bg-slate-200/80 hover:bg-slate-300 text-slate-700 transition-colors cursor-pointer"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <button
                  onClick={() => setActiveVideo(null)}
                  className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white transition-colors cursor-pointer font-bold text-xs flex items-center gap-1 px-3 shadow-xs"
                  title="Close player"
                >
                  <X size={15} />
                  <span>{t.aiVideos.closeModal}</span>
                </button>
              </div>
            </div>

            {/* Video Player Box */}
            <div className="relative bg-black flex-1 flex items-center justify-center overflow-hidden min-h-[300px] sm:min-h-[420px] max-h-[55vh]">
              <video
                src={activeVideo.videoUrl}
                controls
                autoPlay
                muted={isMuted}
                loop
                playsInline
                poster={activeVideo.poster}
                className="w-full h-full max-h-[55vh] object-contain rounded-lg"
              >
                Your browser does not support HTML5 video streaming.
              </video>
            </div>

            {/* Modal Footer / Video Details */}
            <div className="p-3.5 sm:p-4 bg-slate-50 border-t border-slate-200 space-y-2.5 shrink-0">
              <p className="text-[11px] text-slate-600 leading-relaxed line-clamp-2 font-normal">
                {activeVideo.description}
              </p>
              
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200/80">
                <div className="flex flex-wrap gap-1">
                  {activeVideo.tags.map((tag, i) => (
                    <span key={i} className="text-[9px] bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded font-medium">
                      #{tag}
                    </span>
                  ))}
                </div>

                <a
                  href={activeVideo.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[10px] uppercase tracking-wider transition-colors shrink-0 cursor-pointer shadow-xs"
                >
                  <span>Direct URL</span>
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default AiVideoShowcase;

